import React, { useState, useEffect } from 'react';

import { TabList, Typography, Button } from '@hpi-sox/hpstellar-core';
import { ArrowRight } from '@hpi-sox/hpstellar-icons';

import { Helpers } from '../../core/src/helpers';
import useFooterLink from '../../pdp/components-v2/pdp-overview/useFooterLink';
import { getGTMClickAttributes } from '../../metrics/metrics-helpers';
import useDeviceInfo from '../../hooks/useDeviceInfo';

import './aemBanner.less';

const translateBtnColor = theme => {
    if (theme === 'light') return 'white';
    if (theme === 'accent') return 'primary';
    return 'dark';
};

function Banner(props) {
    const {
        title,
        description,
        image,
        buttons,
        backgroundColor,
        viewAllLabel,
        viewAllUrl,
        onLinkClick,
        device,
        single,
        tabLabel,
        categoryLabel,
        hidden,
    } = props;

    const { interceptClick } = useFooterLink(2);
    const isMobile = device === 'mobile';

    let imgUrl = `https://www.hp.com${image.src}`;
    imgUrl = isMobile ? Helpers.addUrlParam(imgUrl, { imwidth: 1024, imdensity: 1 }) : imgUrl;

    return (
        <>
            {single && (
                <div className="aemBanner-single-title" style={{ backgroundColor }}>
                    <Typography className="aemBanner-banner-title" tag="h2" variant="h4">
                        {title}
                    </Typography>
                </div>
            )}
            <div className="aemBanner-banner" style={{ backgroundColor, display: hidden ? 'none' : 'flex' }}>
                <div className="aemBanner-banner-description" style={{ backgroundColor }}>
                    {!single && (
                        <Typography className="aemBanner-banner-title" tag="h2" variant="h4">
                            {title}
                        </Typography>
                    )}
                    <div onClick={interceptClick} dangerouslySetInnerHTML={Helpers.createMarkup(description)} />
                    <div className="aemBanner-banner-buttons">
                        {buttons?.map((buttonProps, index) => {
                            const gtmActions = getGTMClickAttributes(
                                'linkClick',
                                `aemBanner|${categoryLabel}${single ? '' : `|${tabLabel?.toLowerCase().split(' ').join('-')}`}`,
                                buttonProps?.label?.toLowerCase(),
                            );

                            if (index === 0 && isMobile) {
                                return (
                                    <Button
                                        key={(props?.label || 'button') + index}
                                        {...buttonProps}
                                        theme="dark"
                                        endIcon={<ArrowRight size="s" />}
                                        isFixed
                                        variation="tertiary"
                                        style={{ justifyContent: 'flex-start' }}
                                        color={translateBtnColor(buttonProps?.theme)}
                                        {...gtmActions}
                                        onClick={onLinkClick}
                                    >
                                        {buttonProps?.label}
                                    </Button>
                                );
                            }

                            return (
                                <Button
                                    key={(props?.label || 'button') + index}
                                    {...buttonProps}
                                    color={translateBtnColor(buttonProps?.theme)}
                                    {...gtmActions}
                                    onClick={onLinkClick}
                                >
                                    {buttonProps?.label}
                                </Button>
                            );
                        })}
                    </div>
                </div>
                <div className="aemBanner-banner-image" style={{ backgroundColor }}>
                    <img src={imgUrl} alt={image.alt} />
                </div>
            </div>
        </>
    );
}

function AEMBanner({ banners, viewAll, device, location, category }) {
    const [activeTab, setActiveTab] = useState(0);
    const [hideBanners, setHideBanners] = useState(false);
    const [lastLocation, setLastLocation] = useState(null);

    const { isBot } = useDeviceInfo(['isBot']);

    useEffect(() => {
        if (lastLocation === null) {
            return;
        }
        setHideBanners(location.pathname !== lastLocation);
    }, [location, lastLocation]);

    if (hideBanners || !banners || banners.length === 0) return null;

    const onLinkClick = e => {
        setLastLocation(location.pathname);
        setHideBanners(true);
    };

    const { label: viewAllLabel = 'View All', url: viewAllUrl } = viewAll || {};
    const categoryLabel = category?.toLowerCase().split(' ').join('-');

    if (banners.length === 1) {
        const banner = banners[0]?.banner;
        if (!banner) return null;
        return (
            <div className="aemBanner-content">
                <Banner
                    {...banner}
                    viewAllLabel={viewAllLabel}
                    viewAllUrl={viewAllUrl}
                    onLinkClick={onLinkClick}
                    device={device}
                    categoryLabel={categoryLabel}
                    single
                />
            </div>
        );
    }

    const backgroundColor = banners[activeTab]?.banner?.backgroundColor;
    const tabLabel = banners[activeTab]?.tab?.label;
    const banner = banners[activeTab]?.banner;

    return (
        <>
            <div className="aemBanner-nav" style={{ backgroundColor }}>
                <TabList
                    classes={{ dropdown: 'aemBanner-dropdown', tab: 'aemBanner-tab' }}
                    tabs={banners.map(({ tab }) => ({
                        title: tab?.label,
                        gtmCategory: 'linkClick',
                        gtmId: `aemBanner|${categoryLabel}|tab`,
                        gtmValue: `${tab?.label?.toLowerCase().split(' ').join('-')}`,
                    }))}
                    onClick={index => setActiveTab(index)}
                    activeIndex={activeTab}
                    size="large"
                    type="secondary"
                />
                <Button
                    className="aemBanner-viewAll"
                    variation="tertiary"
                    color="dark"
                    size="small"
                    endIcon={<ArrowRight size="s" color="#2c3038" />}
                    isFixed
                    to={viewAllUrl}
                    onClick={onLinkClick}
                    {...getGTMClickAttributes('linkClick', `aemBanner|${categoryLabel}`, 'view-all')}
                >
                    <Typography variant="boldS">{viewAllLabel}</Typography>
                </Button>
            </div>
            <div className="aemBanner-content">
                <Banner
                    {...banner}
                    device={device}
                    tabLabel={tabLabel}
                    categoryLabel={categoryLabel}
                    onLinkClick={onLinkClick}
                />
                {isBot &&
                    banners.map((container, i) => {
                        if (i === activeTab) {
                            return null;
                        }
                        return (
                            <Banner
                                {...container.banner}
                                device={device}
                                tabLabel={tabLabel}
                                categoryLabel={categoryLabel}
                                hidden
                            />
                        );
                    })}
            </div>
        </>
    );
}

export default AEMBanner;
